import React, { useCallback, useEffect, useState } from "react"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { verifyResponse } from "utils/httpRequest"
import {
  approveAiBatch,
  approveAiBatches,
  getAiBatchDetail,
  listAiBatches,
  rejectAiBatch,
  rejectAiBatches,
} from "services/aiPlatform"
import { StyledAiReview } from "./AiReview.style"
import BatchDetailDlg from "./BatchDetailDlg"

const ENTITY_FILTERS = [
  { label: "All Content", value: "" },
  { label: "Programs", value: "program" },
  { label: "Lessons", value: "innercise" },
  { label: "Events", value: "event" },
  { label: "AI Agents", value: "ai_agent" },
]

const STATUS_FILTERS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Executed", value: "EXECUTED" },
  { label: "Failed", value: "FAILED" },
]

const AiReview = ({ embedded = false }) => {
  const [batches, setBatches] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState("")
  const [entityFilter, setEntityFilter] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [detailBatch, setDetailBatch] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const AccessControl = UsePermissionVerification()

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_PLATFORM.AI_REVIEW_LIST,
      ]),
      approveAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_PLATFORM.AI_REVIEW_APPROVE,
      ]),
    })
  }, [AccessControl.UserPermissions])

  const loadBatches = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await listAiBatches({
        status: statusFilter || undefined,
        entityType: entityFilter || undefined,
        pageNo: 1,
        pageSize: 50,
      })
      verifyResponse(response)
      setBatches(response?.list || [])
      setSelectedIds([])
    } catch (error) {
      console.error("Error loading batches:", error)
      setBatches([])
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, entityFilter])

  useEffect(() => {
    if (AccessControl.Access.listAccess) {
      loadBatches()
    }
  }, [loadBatches, AccessControl.Access.listAccess])

  const toggleSelected = (batchId) => {
    setSelectedIds((prev) =>
      prev.includes(batchId)
        ? prev.filter((id) => id !== batchId)
        : [...prev, batchId]
    )
  }

  const toggleSelectAll = () => {
    const pendingBatches = batches.filter((b) => b.status === "PENDING").map((b) => b.id)
    if (selectedIds.length === pendingBatches.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(pendingBatches)
    }
  }

  const openDetail = async (batchId) => {
    setIsLoading(true)
    try {
      const response = await getAiBatchDetail(batchId)
      verifyResponse(response)
      setDetailBatch(response)
      setDetailOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprove = async (batchId) => {
    setIsLoading(true)
    try {
      await approveAiBatch(batchId)
      await loadBatches()
      setDetailOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReject = async (batchId) => {
    setIsLoading(true)
    try {
      await rejectAiBatch(batchId)
      await loadBatches()
      setDetailOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBatchApprove = async () => {
    if (!selectedIds.length) return
    setIsLoading(true)
    try {
      await approveAiBatches(selectedIds)
      await loadBatches()
    } finally {
      setIsLoading(false)
    }
  }

  const handleBatchReject = async () => {
    if (!selectedIds.length) return
    setIsLoading(true)
    try {
      await rejectAiBatches(selectedIds)
      await loadBatches()
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case "PENDING":
        return "status-pending"
      case "APPROVED":
        return "status-approved"
      case "EXECUTED":
        return "status-executed"
      case "REJECTED":
        return "status-rejected"
      case "FAILED":
        return "status-failed"
      default:
        return ""
    }
  }

  const getRiskClass = (risk) => {
    return risk === "HIGH" ? "risk-high" : "risk-normal"
  }

  const pendingBatches = batches.filter((b) => b.status === "PENDING")

  if (!AccessControl.Access.listAccess) {
    return null
  }

  return (
    <StyledAiReview>
      {isLoading && <LoadingScreen />}
      {!embedded && (
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">AI Content Review</h1>
            <p className="page-subtitle">
              Review Claude-proposed CMS changes before they are executed
            </p>
          </div>
        </PageHeading>
      )}

      <div className="page-surface">
        <div className="filters-container">
          <div className="filters">
            <span className="filter-label">Filter by:</span>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              aria-label="Filter by content type"
            >
              {ENTITY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {AccessControl.Access.approveAccess && pendingBatches.length > 0 && (
            <div className="batch-actions">
              <span className="selected-count">
                {selectedIds.length} of {pendingBatches.length} selected
              </span>
              <button
                className="btn-approve"
                type="button"
                onClick={handleBatchApprove}
                disabled={!selectedIds.length}
              >
                ✓ Approve
              </button>
              <button
                className="btn-reject"
                type="button"
                onClick={handleBatchReject}
                disabled={!selectedIds.length}
              >
                ✕ Reject
              </button>
            </div>
          )}
        </div>

        {batches.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-title">No batches found</div>
            <div className="empty-desc">
              {!statusFilter || statusFilter === "PENDING"
                ? "All pending changes have been reviewed"
                : "No changes match the selected filters"}
            </div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="batch-table">
              <thead>
                <tr>
                  <th className="cell-checkbox">
                    {pendingBatches.length > 0 && (
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length === pendingBatches.length &&
                          pendingBatches.length > 0
                        }
                        onChange={toggleSelectAll}
                        aria-label="Select all pending batches"
                      />
                    )}
                  </th>
                  <th>ID</th>
                  <th>Tool</th>
                  <th>Summary</th>
                  <th>Status</th>
                  <th>Risk</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="cell-checkbox">
                      {batch.status === "PENDING" && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(batch.id)}
                          onChange={() => toggleSelected(batch.id)}
                          aria-label={`Select batch ${batch.id}`}
                        />
                      )}
                    </td>
                    <td className="cell-id">#{batch.id}</td>
                    <td className="cell-tool">{batch.toolName}</td>
                    <td className="cell-summary">{batch.changeSummary}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(batch.status)}`}>
                        {batch.status}
                      </span>
                    </td>
                    <td>
                      <span className={`risk-badge ${getRiskClass(batch.riskLevel)}`}>
                        {batch.riskLevel}
                      </span>
                    </td>
                    <td>
                      {batch.createdAt
                        ? new Date(batch.createdAt).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn-view"
                          type="button"
                          onClick={() => openDetail(batch.id)}
                        >
                          View Diff
                        </button>
                        {AccessControl.Access.approveAccess &&
                          batch.status === "PENDING" && (
                            <>
                              <button
                                className="btn-approve"
                                type="button"
                                onClick={() => handleApprove(batch.id)}
                              >
                                Approve
                              </button>
                              <button
                                className="btn-reject"
                                type="button"
                                onClick={() => handleReject(batch.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BatchDetailDlg
        open={detailOpen}
        batch={detailBatch}
        onClose={() => setDetailOpen(false)}
        canApprove={AccessControl.Access.approveAccess}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </StyledAiReview>
  )
}

export default AiReview
